import Button from '@rio-cloud/rio-uikit/Button';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMatch } from 'react-router';

import { useChatComposerFocus } from '@/components/molecule/ChatInput/useChatComposerFocus';
import ExampleQuestionsFilterList from '@/components/molecule/ExampleQuestionsFilterList/ExampleQuestionsFilterList';
import {
  ExampleQuestions,
  ExampleQuestionsContainer,
  FilterListContainer,
} from '@/components/organism/ExampleQuestionsList/ExampleQuestionsList.styles';
import { THandleSendParams } from '@/components/pages/ChatPage/types';
import { useAppSelector } from '@/configuration/setup/hooks';
import { isCurrentRoute } from '@/routes/routeHelper';
import {
  selectIsDemoFlowEnabled,
  selectIsOnboardingMode,
  selectOnboardingStep,
} from '@/store/onboarding/onboardingSlice';
import { selectActiveThreadId, selectIsThreadLoading } from '@/store/thread/threadSlice';

type ExampleQuestionsListProps = {
  isLoading?: boolean;
  handleSend?: (params: THandleSendParams) => Promise<void>;
  shouldShowEmptyState?: boolean;
};

const QUESTION_PANEL_ID = 'question-panel';

const ExampleQuestionsList = ({ isLoading, handleSend, shouldShowEmptyState }: ExampleQuestionsListProps) => {
  const [isVisible, setIsVisible] = useState(false);

  const { hasDraft } = useChatComposerFocus();
  const isOnboardingMode = useAppSelector(selectIsOnboardingMode);
  const enableDemoFlow = useAppSelector(selectIsDemoFlowEnabled);
  const onboardingStep = useAppSelector(selectOnboardingStep);
  const isOnBoardingLocation = isCurrentRoute('/onboarding');
  const activeThreadId = useAppSelector(selectActiveThreadId);
  const isThreadsLoading = useAppSelector(selectIsThreadLoading(activeThreadId));
  const isChatPage = useMatch('/chat');
  const filterListRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const toggleFilterList = useCallback(() => setIsVisible((prev) => !prev), []);

  // INFO: isVisible is the user's intent to have the panel open. The composer fades its bottom slot
  // out while a draft exists, so the panel must also be closed (invisible => inert) to prevent
  // clicks and tab stops on a fully transparent overlay. Clearing the draft restores the intent.
  const isPanelOpen = isVisible && !hasDraft;

  const closePanel = useCallback(() => {
    setIsVisible(false);
    toggleRef.current?.focus();
  }, []);

  useEffect(() => {
    // INFO: Escape is bound at window level, not on the container, so the panel stays closable
    // after focus has left its subtree.
    // isPanelOpen gates the listener; closePanel is the action it performs.
    if (!isPanelOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      // INFO: defaultPrevented lets nested widgets (search field, dialogs) claim Escape first.
      if (event.key === 'Escape' && !event.defaultPrevented) {
        closePanel();
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => window.removeEventListener('keydown', handleEscape);
  }, [isPanelOpen, closePanel]);

  useEffect(() => {
    // INFO: Close panel when navigating away, changing threads, or during loading
    if ((!isChatPage || isThreadsLoading) && !(isOnboardingMode && enableDemoFlow)) {
      setIsVisible(false);
      return;
    }

    // INFO: Onboarding mode controls panel visibility via step
    if (enableDemoFlow && isOnboardingMode) {
      setIsVisible(onboardingStep === 'step-0');
      return;
    }

    // INFO: Auto-open when empty state is shown
    setIsVisible(Boolean(shouldShowEmptyState));
  }, [
    activeThreadId,
    isChatPage,
    isThreadsLoading,
    enableDemoFlow,
    isOnboardingMode,
    onboardingStep,
    shouldShowEmptyState,
  ]);

  return (
    <ExampleQuestionsContainer>
      <ExampleQuestions>
        <Button
          ref={toggleRef}
          data-testid="example-questions-button"
          iconName="rioglyph-question-sign"
          onClick={toggleFilterList}
          disabled={isLoading || isOnBoardingLocation}
          className="btn btn-primary btn-link padding-left-0"
          aria-expanded={isPanelOpen}
          aria-controls={QUESTION_PANEL_ID}
        >
          <FormattedMessage id="chatPage.sidebar.faq.label" />
        </Button>
      </ExampleQuestions>
      <FilterListContainer
        id={QUESTION_PANEL_ID}
        data-testid="example-questions-panel"
        ref={filterListRef}
        className={isPanelOpen ? 'visible' : ''}
        inert={isPanelOpen ? undefined : ''}
      >
        <ExampleQuestionsFilterList
          handleSend={handleSend}
          isOnboarding={enableDemoFlow && isOnboardingMode}
          isLoading={isLoading}
          isPanelOpen={isPanelOpen}
          onClosePanel={closePanel}
        />
      </FilterListContainer>
    </ExampleQuestionsContainer>
  );
};

export default ExampleQuestionsList;
